import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useApiLanguage } from './useApiLanguage';
import { orderService } from '../services/orderService';
import type {
    CreateOrderRequestDto,
    CreateOrderResponseDto,
    DeleteOrderResponseDto,
    GetOrderDetailResponseDto,
    GetOrderListRequestDto,
    GetOrderListResponseDto,
    GetOrderStatisticsResponseDto,
    UpdateOrderRequestDto,
    UpdateOrderResponseDto,
} from '../types/api/order';

/** `Orders.tsx` 학생·게스트 경로와 동일 — 무효화 시 이 prefix 사용 */
export const ordersPageMyQueryKeyPrefix = ['orders', 'my'] as const;

/** 런타임에 과거 코드·스프레드로 language 가 섞여도 목록 API/캐시 키에서 제거 */
function orderListParamsForApi(params: GetOrderListRequestDto): GetOrderListRequestDto {
    const p = params as GetOrderListRequestDto & { language?: string };
    const { language: _omit, ...rest } = p;
    return rest;
}

export const orderKeys = {
    all: ['order'] as const,
    lists: () => [...orderKeys.all, 'list'] as const,
    ordersPageMy: (page: number) => [...ordersPageMyQueryKeyPrefix, page] as const,
    list: (params: GetOrderListRequestDto) => [...orderKeys.lists(), orderListParamsForApi(params)] as const,
    details: () => [...orderKeys.all, 'detail'] as const,
    detail: (orderId: number) => [...orderKeys.details(), orderId] as const,
    statistics: (startDate: string, endDate: string) => [...orderKeys.all, 'statistics', startDate, endDate] as const,
};

export const useOrderList = (
    params: GetOrderListRequestDto,
    options?: Omit<UseQueryOptions<GetOrderListResponseDto>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: orderKeys.list(params),
        queryFn: () => orderService.getList(orderListParamsForApi(params)),
        ...options,
    });
};

export const useOrderDetail = (
    orderId: number,
    options?: Omit<UseQueryOptions<GetOrderDetailResponseDto>, 'queryKey' | 'queryFn'>
) => {
    const language = useApiLanguage();
    return useQuery({
        queryKey: [...orderKeys.detail(orderId), language],
        queryFn: () => orderService.getDetail(orderId, language),
        enabled: orderId > 0 && (options?.enabled ?? true),
        ...options,
    });
};

export const useOrderStatistics = (
    startDate: string,
    endDate: string,
    options?: Omit<UseQueryOptions<GetOrderStatisticsResponseDto>, 'queryKey' | 'queryFn'>
) =>
    useQuery({
        queryKey: orderKeys.statistics(startDate, endDate),
        queryFn: () => orderService.getStatistics(startDate, endDate),
        enabled: !!startDate && !!endDate && (options?.enabled ?? true),
        ...options,
    });

export const useCreateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<CreateOrderResponseDto, Error, CreateOrderRequestDto>({
        mutationFn: (data) => orderService.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: orderKeys.lists(), exact: false });
            queryClient.invalidateQueries({ queryKey: [...ordersPageMyQueryKeyPrefix], exact: false });
            queryClient.invalidateQueries({ queryKey: [...orderKeys.all, 'statistics'], exact: false });
        },
    });
};

export const useUpdateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<UpdateOrderResponseDto, Error, { orderId: number; data: UpdateOrderRequestDto }>({
        mutationFn: ({ orderId, data }) => orderService.update(orderId, data),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({ queryKey: orderKeys.lists(), exact: false });
            queryClient.invalidateQueries({ queryKey: [...ordersPageMyQueryKeyPrefix], exact: false });
            queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
            queryClient.invalidateQueries({ queryKey: [...orderKeys.all, 'statistics'], exact: false });
        },
    });
};

export const useDeleteOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<DeleteOrderResponseDto, Error, number>({
        mutationFn: (orderId) => orderService.delete(orderId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: orderKeys.lists(), exact: false });
            queryClient.invalidateQueries({ queryKey: [...ordersPageMyQueryKeyPrefix], exact: false });
            queryClient.invalidateQueries({ queryKey: [...orderKeys.all, 'statistics'], exact: false });
        },
    });
};
